import { isNetworkError, isUnauthorized } from "../api/client";
import type { Seller } from "./seller";

/**
 * The seller sign-in state machine, expressed as one pure-ish function so every branch is
 * testable without a browser.
 *
 * Under canon decision `D-008` this is a short machine, and that is the point. Sign-in
 * itself happens in the browser's address bar — `app.dealbid.app` to core to Google and
 * back, with the cookie already set (`contracts/API.md` § Seller sign-in shape). By the
 * time this function runs there is nothing left to negotiate: either core recognises the
 * cookie or it does not.
 *
 * The refusal flag is read from the return address, never trusted over a live session. A
 * founder who has a working session in this browser sees their desk even if the tab's URL
 * still carries `?error=SIGN_IN_FAILED` from an earlier attempt — the cookie is the fact,
 * the query parameter is only a message.
 */

export type SignedOutReason =
  /** No session cookie, or core no longer honours it. */
  | "no-session"
  /**
   * Core refused a sign-in and sent the founder back saying so. One reason, because core
   * merges every cause into one redirect on purpose; the app cannot and must not
   * distinguish them.
   */
  | "sign-in-failed";

export type SessionErrorReason =
  /** Dealbid could not be reached at all. */
  | "network"
  | "unexpected";

export type SessionOutcome =
  | { status: "signed-in"; seller: Seller }
  | { status: "signed-out"; reason: SignedOutReason }
  | { status: "error"; reason: SessionErrorReason };

export interface SessionDependencies {
  /** Whether core's return address said the attempt was refused. Already read by the caller. */
  signInRefused: boolean;
  fetchSeller: () => Promise<Seller | null>;
}

function failure(error: unknown): SessionOutcome {
  if (isNetworkError(error)) return { status: "error", reason: "network" };
  return { status: "error", reason: "unexpected" };
}

export async function resolveSession(deps: SessionDependencies): Promise<SessionOutcome> {
  const signedOut = (): SessionOutcome => ({
    status: "signed-out",
    reason: deps.signInRefused ? "sign-in-failed" : "no-session",
  });

  try {
    const seller = await deps.fetchSeller();
    return seller ? { status: "signed-in", seller } : signedOut();
  } catch (error) {
    // A refused identity is not a fault to retry — it is simply no session.
    if (isUnauthorized(error)) return signedOut();
    return failure(error);
  }
}
